import { inject, Injectable } from '@angular/core';
import { CurrentUserService, DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { ChatApiService } from '@rma/dashboard/generic/api/chat-data';
import { ChameleonService } from '@rma/dashboard/generic/util/chameleon';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { HeapService } from '@rma/dashboard/generic/util/heap';
import { HubSpotService } from '@rma/dashboard/generic/util/hubspot';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { retryWithDelay } from '@rma/generic/rxjs/operators/retry-with-delay';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { throwIf } from '@rma/generic/rxjs/operators/throw-if';
import { differenceInDays } from 'date-fns';
import { catchError, combineLatest, debounceTime, EMPTY, filter, map, startWith } from 'rxjs';
import { buildChameleonCompany } from './build-chameleon-company';

@Injectable({
  providedIn: 'root',
})
export class ThirdPartyUserIdentificationService {
  private readonly chatApiService = inject(ChatApiService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly userService = inject(CurrentUserService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly environment = inject(Environment);

  private readonly heapService = inject(HeapService, { optional: true });
  private readonly chameleonService = inject(ChameleonService, {
    optional: true,
  });
  private readonly hubSpotService = inject(HubSpotService, { optional: true });

  private readonly currentUser$ = this.userService.currentUser$.pipe(rmaShareReplay());

  private readonly userData$ = combineLatest({
    user: this.currentUser$.pipe(
      nonNullable(),
      filter((c) => !!c.userId),
    ),
    userChatData: this.chatApiService.userChatData$,
  }).pipe(rmaShareReplay());

  public initialise() {
    this.initialiseHeap();
    this.initialiseHubSpot();
    this.initialiseChameleon();
  }

  private initialiseHeap() {
    if (this.heapService && this.environment.heapAppId) {
      this.heapService.initialiseHeap(this.environment.heapAppId);

      // addDynamicUserProperties
      this.userData$.subscribe(({ user, userChatData }) => {
        // because we don't handle log-out, before we identify anybody, lets reset the ident
        // this makes sure we don't join two users together, but if we just re-ident as the same user anyway, things will be fine so 🧙‍♀️
        this.heapService?.resetIdentity();

        if (!user.isImpersonating) {
          this.heapService?.identify(user.userId);

          const { user_id, user_hash, app_id, rma_locale, ...userProperties } = userChatData;

          // If you ever get `TypeError: window.heap.push is not a function` ---> https://github.com/segmentio/analytics.js/issues/605
          this.heapService?.addUserProperties({
            ...userProperties,
            rma_user_type: user.userType,
          });
        }

        this.heapService?.addUserProperties({
          'rma.authenticatedViaRadmin': user.isImpersonating,
        });
      });

      // setEventProperties
      this.currentSubscriptionService.currentSubscriptionSummary$
        .pipe(
          map((s) => ({
            rma_sub_is_free: s?.isFreeSubscription,
            rma_sub_tier: s?.subscriptionTier,
          })),
        )
        .subscribe((summary) => this.heapService?.addEventProperties(summary));
    }
  }

  private initialiseHubSpot() {
    if (this.hubSpotService) {
      const user$ = this.currentUser$.pipe(
        nonNullable(),
        map(({ email, isImpersonating, userId }) => ({
          email,
          isImpersonating,
          userId,
        })),
        throwIf(
          () => !this.hubSpotService?.window?._hsq,
          () => 'hubspot not loaded yet',
        ),
        retryWithDelay(200, 4),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
        rmaShareReplay(),
      );

      // identify user
      user$.subscribe(({ email, isImpersonating, userId }) => {
        // remove existing tracking info
        this.hubSpotService?.window?._hsq.push(['revokeCookieConsent']);

        if (this.hubSpotService?.trackUser(isImpersonating, userId)) {
          this.hubSpotService?.window?._hsq.push(['identify', { email }]);
        }
      });

      this.hubSpotService.initialiseHubspot(user$.pipe(map(({ isImpersonating, userId }) => ({ isImpersonating, userId }))));
    }
  }

  private initialiseChameleon() {
    const chameleon = this.chameleonService;
    if (chameleon && this.environment.chameleonToken) {
      combineLatest({
        user: this.currentUser$.pipe(
          nonNullable(),
          filter((c) => !!c.userId),
        ),
        userChatData: this.chatApiService.userChatData$,
        profile: this.profileContextService.profileContext$.pipe(nonNullable()),
        subscription: this.currentSubscriptionService.currentSubscription$.pipe(startWith(null)),
      })
        .pipe(
          // profileContext and currentSubscription both derive from the same profile switch;
          // subscription arrives a tick later after its HTTP fetch, so debounce absorbs the glitch
          // and avoids identifying Chameleon twice with a transient stale-plan value.
          debounceTime(250),
          throwIf(
            () => !chameleon.isReady(),
            () => 'chameleon not loaded yet',
          ),
          retryWithDelay(200, 4),
          catchError((error) => {
            console.log(error);
            return EMPTY;
          }),
        )
        .subscribe(({ user, userChatData, profile, subscription }) => {
          chameleon.registerNavigationHandler();
          chameleon.identify(
            user.userId,
            {
              rma_authenticated_via_radmin: user.isImpersonating,
              rma_user_email: user.email,
              rma_user_type: user.userType,
              rma_days_since_last_login: differenceInDays(new Date(), user.lastLoggedIn),
            },
            buildChameleonCompany(this.environment.tenant, profile, subscription, userChatData),
          );
        });
    }
  }
}
